import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { AchievementsPage } from "@/components/engagement/Achievements";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Medal } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { OneLine } from "@/components/ui/one-line";
import { Skeleton } from "@/components/ui/skeleton";
import { useAchievements } from "@/hooks/useEngagement";
import { AchievementIcon } from "@/components/engagement/Achievements";
import { WeekShare } from "@/components/engagement/WeekShare";
import { TIER_COLOR, achievementAmount, nextNeed, type Achievement } from "@/lib/engagement.core";

/****************
 * ACHIEVEMENTS IN THE HEADER, BESIDE THE BELL AND THE TRADE TRAY.
 * 2026-10-25, Guillaume: "have it into the header, just like the Notification
 * and the open trade trails, rather than on the dashboard ... Symbol = Trophy".
 *
 * The trophy opens the user's own achievements: their goal first, then the
 * showcase they picked (or, with none picked, the three closest to the next
 * tier). The full page, where both are set up, is one click away.
 * Phone twin: mobile-app/src/components/engagement/AchievementsTray.tsx.
 ****************/

/** The goal, then the showcase (or the closest three), at most four. */
export function chosenAchievements(data: { achievements?: Achievement[]; goal?: string; goals?: string[]; showcase?: string[] } | undefined) {
  const all = data?.achievements ?? [];
  const goalKeys = data?.goals ?? (data?.goal ? [data.goal] : []);
  const goals = goalKeys.map((k) => all.find((a) => a.key === k)).filter(Boolean) as Achievement[];
  const goal = goals[0];
  const chosen = (data?.showcase ?? []).map((k) => all.find((a) => a.key === k)).filter(Boolean) as Achievement[];
  const closest = [...all]
    .filter((a) => nextNeed(a) !== null && a.key !== goal?.key)
    .sort((a, b) => (b.have ?? 0) / (nextNeed(b) || 1) - (a.have ?? 0) / (nextNeed(a) || 1))
    .slice(0, 3);
  const rest = (chosen.length ? chosen : closest).filter((a) => !goalKeys.includes(a.key));
  const rows = [...goals, ...rest].slice(0, 6);
  return { goal, goals: goalKeys, rows };
}

/** Any screen can open the full Achievements page (the Pros lock, round 8 #239). */
export const OPEN_ACHIEVEMENTS_EVENT = "copyfi:open-achievements";
export const openAchievementsPage = () => window.dispatchEvent(new Event(OPEN_ACHIEVEMENTS_EVENT));

/** The achievements the user follows (up to five), in their order (round 8 #214). */
export function followedAchievements(data: { achievements?: Achievement[]; goal?: string; goals?: string[] } | undefined): Achievement[] {
  const all = data?.achievements ?? [];
  const keys = data?.goals ?? (data?.goal ? [data.goal] : []);
  return keys.map((k) => all.find((a) => a.key === k)).filter(Boolean).slice(0, 5) as Achievement[];
}

export function AchievementsTray() {
  const { t: tl } = useTranslation("learn");
  const [open, setOpen] = useState(false);
  // The full set, as a pop-up from here only (Guillaume 10-25: "this is where the
  // page should be accessible from, it should just be a pop-up").
  const [setup, setSetup] = useState(false);
  const openSetup = () => { setOpen(false); setSetup(true); };
  useEffect(() => {
    const show = () => { setOpen(false); setSetup(true); };
    window.addEventListener(OPEN_ACHIEVEMENTS_EVENT, show);
    return () => window.removeEventListener(OPEN_ACHIEVEMENTS_EVENT, show);
  }, []);
  const q = useAchievements();
  // Only the ones I follow, up to five (Guillaume 10-27, round 8 #214).
  const rows = followedAchievements(q.data);
  return (
    <>
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="relative h-8 w-8 p-0" aria-label={tl("ach.title")} title={tl("ach.title")} data-tour="header-achievements">
          {/* A medal, not the competition's trophy (round 9 #301). */}
          {/* Round 11 #501: the medal's glyph reads small at 20, so 22, in the same 32-px square as its neighbours. */}
          <Medal size={22} />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[360px] p-0">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between gap-3">
          <h3 className="font-semibold text-sm">{tl("ach.mineTitle")}</h3>
          <WeekShare />
        </div>
        <div className="p-3 space-y-2">
          {q.isLoading ? <Skeleton className="h-16 w-full" /> : rows.length === 0 ? (
            <p className="text-xs text-muted-foreground px-1">{tl("ach.mineEmpty")}</p>
          ) : rows.map((a) => {
            const need = nextNeed(a);
            return (
              <button type="button" key={a.key} onClick={openSetup}
                className="w-full text-start flex items-center gap-3 rounded-lg border border-border/60 p-3 hover:border-primary/40 transition-colors min-w-0">
                <AchievementIcon a={a} size={26} />
                <div className="min-w-0 flex-1">
                  <OneLine className="text-sm font-medium">
                    {tl(`ach.k.${a.key}.title`)}
                  </OneLine>
                  {need !== null ? (
                    <>
                      <div className="h-1 rounded-full bg-white/10 mt-1.5 overflow-hidden"><div className="h-1 bg-primary" style={{ width: `${Math.min(100, (100 * (a.have ?? 0)) / need)}%` }} /></div>
                      <OneLine className="text-[11px] text-muted-foreground mt-1">{tl("ach.next", { have: achievementAmount(a, a.have ?? 0), need: achievementAmount(a, need), tier: tl(`ach.tier.${a.tier + 1}`) })}</OneLine>
                    </>
                  ) : <OneLine className="text-[11px]" style={{ color: TIER_COLOR[3] }}>{tl("ach.max")}</OneLine>}
                </div>
              </button>
            );
          })}
        </div>
        <div className="px-4 py-2.5 border-t border-border">
          <Button size="sm" variant="outline" className="w-full" onClick={openSetup} data-tour="ach-open-page">{tl("ach.openPage")}</Button>
        </div>
      </PopoverContent>
    </Popover>
    <Dialog open={setup} onOpenChange={setSetup}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogTitle className="sr-only">{tl("ach.title")}</DialogTitle>
        <AchievementsPage embedded />
      </DialogContent>
    </Dialog>
    </>
  );
}
